import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, ElementRef, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import * as Feather from 'feather-icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Router } from '@angular/router';
import { HeaderService } from '../../../services/header/header.service';
import { Subscription } from 'rxjs';
import { ClientService } from '../../../services/Client/client.service';
import { Client } from '../../../models/client';
import { PaginatedResponse } from '../../../models/paginatedResponse';
import { ClienEventService } from '../../../services/Client/clien-event.service';

@Component({
    selector: 'app-clients',
    templateUrl: './clients.component.html',
    styleUrls: ['./clients.component.scss']
})
export class ClientsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('importModal', { static: false }) importModal!: TemplateRef<any>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    selectedFile: File | null = null;
    activeClientCount: number = 0;
    archivedClientCount: number = 0;
    stationId: number = 0;
    userId: number = 0;
    private subscriptions: Subscription[] = [];
    private validPhoneTypes: string[] = ['mobile', 'fixe', 'fax', 'whatsapp', 'viber', 'support'];
    constructor(
        private modalService: NgbModal,
        private toastr: ToastrService,
        private clientService: ClientService,
        private clientEventService: ClienEventService,
        private router: Router,
        private headerService: HeaderService
    ) {}

    ngOnInit(): void {
        this.loadUserData();
        this.loadClientCounts();
        if (this.router.url === '/tiers/clients') {
            this.router.navigate(['/tiers/clients/actif']);
        }
        this.headerService.setConfig({
            title: 'Clients',
            buttons: [
                {
                    label: 'Exporter',
                    type: 'dropdown',
                    dropdownItems: [
                        {
                            label: 'PDF',
                            action: () => this.exportTable('pdf'),
                            icon: '../../../assets/images/logo/pdf_logo.png'
                        },
                        {
                            label: 'CSV',
                            action: () => this.exportTable('csv'),
                            icon: '../../../assets/images/logo/csv_logo.png'
                        },
                        {
                            label: 'XLSX',
                            action: () => this.exportTable('xlsx'),
                            icon: '../../../assets/images/logo/xlsx_logo.png'
                        }
                    ]
                },
                {
                    label: 'Importer',
                    action: () => this.openImportModal(),
                    type: 'secondary'
                }
            ]
        });
        this.subscriptions.push(
            this.clientEventService.onClientArchiveEvent().subscribe(({ client, is_archive }) => {
                //console.log(`ClientEvent: Client ${client.nom_prenom} ${is_archive ? 'archived' : 'unarchived'}`);
                // Update counts locally
                if (is_archive) {
                    this.activeClientCount = Math.max(0, this.activeClientCount - 1);
                    this.archivedClientCount++;
                } else {
                    this.archivedClientCount = Math.max(0, this.archivedClientCount - 1);
                    this.activeClientCount++;
                }
            })
        );
    }

    ngAfterViewInit() {
        setTimeout(() => Feather.replace(), 0);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    loadUserData() {
        const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
        this.stationId = user?.stations?.[0]?.id || 0;
        this.userId = user?.id || 0;
        //console.log('ClientsComponent User Data:', user, 'Station ID:', this.stationId);
        if (!this.stationId) {
            this.toastr.error('Aucune station trouvée. Veuillez créer une station.', 'Erreur');
            this.router.navigate(['/create-station']);
        }
    }

    loadClientCounts() {
        if (!this.stationId) return;
        const activeSub = this.clientService.getActiveClient(this.stationId, 1, 10).subscribe({
            next: (response: PaginatedResponse<Client>) => {
                //console.log('ClientsComponent getActiveClient Response:', response);
                this.activeClientCount = response.meta?.total || 0;
            },
            error: (error) => {
                this.toastr.error(error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement du nombre de clients actifs', 'Erreur');
                console.error('ClientsComponent Error:', error);
            }
        });
        const archivedSub = this.clientService.getArchivedClient(this.stationId, 1, 1).subscribe({
            next: (response: PaginatedResponse<Client>) => {
                //console.log('ClientsComponent getArchivedClient Response:', response);
                this.archivedClientCount = response.meta?.total || 0;
            },
            error: (error) => {
                this.toastr.error(error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement du nombre de clients archivés', 'Erreur');
                console.error('ClientsComponent Error:', error);
            }
        });
        this.subscriptions.push(activeSub, archivedSub);
    }

    openImportModal() {
        this.selectedFile = null;
        this.modalService.open(this.importModal, { centered: true, size: 'xl' });
        setTimeout(() => Feather.replace(), 1);
    }

    triggerFileInput(event: MouseEvent) {
        event.preventDefault();
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.click();
        } else {
            console.error('fileInput is not available');
            this.toastr.error('Erreur d\'accès au sélecteur de fichier', 'Erreur');
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            //console.log('Selected file:', this.selectedFile.name);
            if (this.selectedFile.size > 1024 * 1024) {
                this.toastr.error('Fichier trop volumineux (max 1 Mo)', 'Erreur');
                this.selectedFile = null;
                input.value = '';
            } else if (!this.selectedFile.name.endsWith('.xlsx')) {
                this.toastr.error('Seuls les fichiers .xlsx sont autorisés', 'Erreur');
                this.selectedFile = null;
                input.value = '';
            } else {
                this.toastr.success('Fichier sélectionné avec succès', 'Succès');
            }
        }
    }

    importFile() {
        if (!this.selectedFile) {
            this.toastr.error('Aucun fichier sélectionné', 'Erreur');
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheet];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                const expectedHeaders = ['RÉFÉRENCE', 'NOM DU CLIENT', 'EMAIL', 'TYPE', 'CIVILITÉ', 'TÉLÉPHONE', 'CODE PAYS', 'TYPE TÉLÉPHONE'];
                const headers = jsonData[0]?.map((h: string) => h?.toString().trim());
                if (!headers || !expectedHeaders.every((h, i) => headers[i] === h)) {
                    this.toastr.error('Le fichier doit contenir les colonnes : ' + expectedHeaders.join(', '), 'Erreur');
                    return;
                }

                const validClientTypes = ['Individuel', 'Professionnel'];
                const validCivilities = ['Homme', 'Femme'];
                const validPhoneTypes = ['mobile', 'fixe', 'fax', 'whatsapp', 'viber', 'support'];

                let importedCount = 0;
                const totalRows = jsonData.slice(1).length;
                const errors: string[] = [];


                for (let i = 0; i < jsonData.slice(1).length; i++) {
                    const row = jsonData[i + 1];
                    const nom_prenom = row[1]?.toString().trim();
                    const type = row[3]?.toString().trim();
                    const civilite = row[4]?.toString().trim();


                    if (!nom_prenom) {
                        errors.push(`Ligne ${i + 2} : Nom du client manquant.`);
                        continue;
                    }
                    if (!type || !validClientTypes.includes(type)) {
                        errors.push(`Ligne ${i + 2} : Type de client invalide (${type}). Doit être "Individuel" ou "Professionnel".`);
                        continue;
                    }
                    if (!civilite || !validCivilities.includes(civilite)) {
                        errors.push(`Ligne ${i + 2} : Civilité invalide (${civilite}). Doit être "Homme" ou "Femme".`);
                        continue;
                    }

                    const client: Client = {
                        id: 0,
                        identifiant_unique: row[0]?.toString().trim() || null,
                        nom_prenom,
                        type,
                        civilite,
                        emails: row[2] && row[2].toString().trim() ? [{
                            email: row[2].toString().trim(),
                            user_id: this.userId,
                            client_id: 0,
                            station_id: this.stationId
                        }] : [],
                        telephones: row[5] && row[6] ? [{
                            id: 0,
                            type: row[7] && validPhoneTypes.includes(row[7].toString().trim()) ? row[7].toString().trim() : 'mobile',
                            num_tel: row[5].toString().trim(),
                            code_pays: row[6].toString().trim(),
                            station_id: this.stationId,
                            user_id: this.userId,
                            client_id: 0
                        }] : [],
                        station_id: this.stationId,
                        user_id: this.userId,
                        is_archive: false,
                        created_at: undefined,
                        updated_at: undefined,
                        adresses: [],
                        informations: []
                    };

                    const sub = this.clientService.addClient(client).subscribe({
                        next: (addedClient) => {
                            importedCount++;
                            this.clientEventService.emitClientArchiveEvent(addedClient, false);
                            if (importedCount + errors.length === totalRows) {
                                this.finalizeImport(importedCount, errors);
                            }
                        },
                        error: (error) => {
                            const errorMessage = error.error?.errors?.map((err: any) => err.message).join(', ') || `Erreur lors de l'ajout du client ${client.nom_prenom}`;
                            errors.push(`Ligne ${i + 2} : ${errorMessage}`);
                            console.error(`Import Error for client ${client.nom_prenom}:`, JSON.stringify(error, null, 2));
                            if (importedCount + errors.length === totalRows) {
                                this.finalizeImport(importedCount, errors);
                            }
                        }
                    });
                    this.subscriptions.push(sub);
                }

                if (totalRows === 0) {
                    this.toastr.error('Aucune donnée valide trouvée dans le fichier', 'Erreur');
                    this.selectedFile = null;
                    if (this.fileInput?.nativeElement) {
                        this.fileInput.nativeElement.value = '';
                    }
                }
            };

            reader.onerror = () => {
                this.toastr.error('Erreur lors de la lecture du fichier', 'Erreur');
            };

            reader.readAsArrayBuffer(this.selectedFile);
        } catch (error) {
            console.error('Import Error:', error);
            this.toastr.error('Erreur lors de l\'importation du fichier', 'Erreur');
        }
    }

    private finalizeImport(importedCount: number, errors: string[]) {
        if (importedCount > 0) {
            this.toastr.success(`Importation réussie : ${importedCount} client(s) ajouté(s)`, 'Succès');
            this.loadClientCounts();
        }
        if (errors.length > 0) {
            this.toastr.error(`Erreur(s) lors de l'importation : ${errors.join('; ')}`, 'Erreur');
        }
        this.selectedFile = null;
        if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
        }
    }

    downloadTemplate() {
        try {
            const templateData = [{
                'RÉFÉRENCE': '',
                'NOM DU CLIENT': '',
                'EMAIL': '',
                'TYPE': '',
                'CIVILITÉ': '',
                'TÉLÉPHONE': '',
                'CODE PAYS': '',
                'TYPE TÉLÉPHONE': '' // Nouvelle colonne
            }];
            const worksheet = XLSX.utils.json_to_sheet(templateData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
            XLSX.writeFile(workbook, 'client_template.xlsx');
            this.toastr.success('Modèle téléchargé', 'Succès');
        } catch (error) {
            console.error('Template Download Error:', error);
            this.toastr.error('Erreur lors du téléchargement du modèle', 'Erreur');
        }
    }

    exportTable(format: string) {
        if (!this.stationId) {
            this.toastr.error('Aucune station sélectionnée', 'Erreur');
            return;
        }

        const sub = this.clientService.getActiveClient(this.stationId, 1, 1000).subscribe({
            next: (response: PaginatedResponse<Client>) => {
                const tableData: Client[] = response.data || [];
                if (!tableData || tableData.length === 0) {
                    this.toastr.error('Aucune donnée à exporter', 'Erreur');
                    return;
                }

                switch (format) {
                    case 'pdf':
                        this.exportToPDF(tableData);
                        break;
                    case 'csv':
                        this.exportToCSV(tableData);
                        break;
                    case 'xlsx':
                        this.exportToXLSX(tableData);
                        break;
                    default:
                        this.toastr.error('Format non supporté', 'Erreur');
                }
            },
            error: (error) => {
                this.toastr.error(error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement des clients pour exportation', 'Erreur');
                console.error('Error:', error);
            }
        });
        this.subscriptions.push(sub);
    }

    private exportToPDF(data: Client[]) {
        try {
            const doc = new jsPDF();
            autoTable(doc, {
                columns: [
                    { header: 'RÉFÉRENCE', dataKey: 'identifiant_unique' },
                    { header: 'NOM DU CLIENT', dataKey: 'nom_prenom' },
                    { header: 'REVENUS', dataKey: 'revenue' },
                    { header: 'MATRICULE FISCAL', dataKey: 'matricule' }
                ],
                body: data.map(item => ({
                    identifiant_unique: item.identifiant_unique || item.id?.toString() || 'N/A',
                    nom_prenom: `${item.nom_prenom || 'N/A'} (${item.emails[0] || 'N/A'})`, // Changed from email
                    revenue: 'N/A',
                    matricule: item.identifiant_unique || item.id?.toString() || 'N/A'
                })),
                theme: 'striped',
                headStyles: { fillColor: [115, 103, 240] },
                styles: { fontSize: 10 }
            });
            doc.save('clients.pdf');
            this.toastr.success('Exportation PDF réussie', 'Succès');
        } catch (error) {
            console.error('PDF Export Error:', error);
            this.toastr.error('Erreur lors de l\'exportation PDF', 'Erreur');
        }
    }

    private exportToCSV(data: Client[]) {
        try {
            const headers = ['RÉFÉRENCE,NOM DU CLIENT,EMAIL,TYPE,CIVILITÉ,TÉLÉPHONE,CODE PAYS'];
            const rows = data.map(item =>
                `"${item.identifiant_unique || item.id || 'N/A'}","${item.nom_prenom || 'N/A'}","${item.emails[0] || 'N/A'}","${item.type || 'N/A'}","${item.civilite || 'N/A'}","${item.telephones?.[0]?.num_tel || 'N/A'}","${item.telephones?.[0]?.code_pays || 'N/A'}"`
            );
            const csvContent = headers.concat(rows).join('\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'clients.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.toastr.success('Exportation CSV réussie', 'Succès');
        } catch (error) {
            console.error('CSV Export Error:', error);
            this.toastr.error('Erreur lors de l\'exportation CSV', 'Erreur');
        }
    }

    private exportToXLSX(data: Client[]) {
        try {
            const worksheetData = data.map(item => ({
                'RÉFÉRENCE': item.identifiant_unique || item.id?.toString() || 'N/A',
                'NOM DU CLIENT': item.nom_prenom || 'N/A',
                'EMAIL': item.emails[0] || 'N/A', // Changed from email
                'TYPE': item.type || 'N/A',
                'CIVILITÉ': item.civilite || 'N/A',
                'TÉLÉPHONE': item.telephones?.[0]?.num_tel || 'N/A',
                'CODE PAYS': item.telephones?.[0]?.code_pays || 'N/A'
            }));
            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients');
            XLSX.writeFile(workbook, 'clients.xlsx');
            this.toastr.success('Exportation XLSX réussie', 'Succès');
        } catch (error) {
            console.error('XLSX Export Error:', error);
            this.toastr.error('Erreur lors de l\'exportation XLSX', 'Erreur');
        }
    }
}
